import { ButtonBase, ButtonBaseProps, SxProps, Theme } from '@mui/material';
import { ReactNode } from 'react';

import {
  getSelectableItemSelectedSx,
  selectableItemColumnSx,
  SelectableItemOrientation,
  selectableItemRootSx,
  SelectableItemSelectedAnchor,
} from './selectable-item-styles';

export type { SelectableItemOrientation, SelectableItemSelectedAnchor };

export type SelectableItemProps = Omit<ButtonBaseProps, 'sx' | 'children'> & {
  /** Whether this item is the currently selected one. */
  selected?: boolean;
  /** Edge for the inset primary bar when selected. Defaults to `left`. */
  selectedAnchor?: SelectableItemSelectedAnchor;
  /** Content flow inside the item. Defaults to `row` (list rows). */
  orientation?: SelectableItemOrientation;
  children?: ReactNode;
  sx?: SxProps<Theme>;
};

/**
 * Interactive surface for selectable rows/tiles in master–detail or sidebar lists.
 * Supports idle, hover, selected, and selected+hover states without media-card layout.
 */
export function SelectableItem({
  selected = false,
  selectedAnchor = 'left',
  orientation = 'row',
  children,
  sx,
  disabled,
  ...buttonProps
}: SelectableItemProps) {
  return (
    <ButtonBase
      disableRipple
      disabled={disabled}
      {...buttonProps}
      sx={
        [
          selectableItemRootSx,
          ...(orientation === 'column' ? [selectableItemColumnSx] : []),
          ...(selected ? [getSelectableItemSelectedSx(selectedAnchor)] : []),
          ...(sx !== undefined ? (Array.isArray(sx) ? sx : [sx]) : []),
        ] as SxProps<Theme>
      }
    >
      {children}
    </ButtonBase>
  );
}
